import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { Observable, Subject, debounceTime, takeUntil } from 'rxjs';
import { TIME_OF_DAY_DESCRIPTION } from 'src/app/pages/agreements/constants/rate-description.constants';
import {
  PriceLimitAlertEnum,
  PriceLimitAlertToDEnum,
} from 'src/app/pages/agreements/enums/price-limit-alert-type.enum';
import { LastMinuteJobRateFormPropertiesToUpdate } from 'src/app/pages/flex/pages/jobs/types/lmj-minute-job-rate-form-update';
import { JobDetail } from 'src/app/shared/models/gql/job-detail.model';
import { NetRate, PriceLimitAlertData } from 'src/app/shared/models/price-limit-alert-data.model';
import { JobsService } from 'src/app/shared/services/jobs.service';
import { MinimumRateService } from 'src/app/shared/services/minimum-rate.service';
import { SharedModule } from 'src/app/shared/shared.module';

@Component({
  standalone: true,
  imports: [SharedModule, ReactiveFormsModule, CommonModule, IonicModule],
  selector: 'last-minute-job-rate',
  templateUrl: './last-minute-job-rate.component.html',
  styleUrls: ['./last-minute-job-rate.component.scss'],
})
export class LastMinuteJobRateComponent implements OnInit, OnDestroy {
  // job
  @Input() job: JobDetail;

  // The support terms form change event emitter
  @Output() rateChange = new EventEmitter();

  // data to be passed to the price limit alert component for segment event use
  priceLimitAlertData: PriceLimitAlertData;

  /** minimum agreement rate allowed in the platform */
  minimumRate: number;

  // form group for last minute job rate form
  form: FormGroup;

  // subject for onDestroy, used to unsubscribe from subscriptions
  onDestroySubject = new Subject<void>();

  constructor(
    private jobsService: JobsService,
    private minimumRateService: MinimumRateService,
  ) {}

  ngOnInit() {
    this.setUpForm();
    this.setPriceLimitAlertData();
  }

  /**
   * Update the subject on destroying the component.
   */
  ngOnDestroy(): void {
    this.onDestroySubject.next();
    this.onDestroySubject.complete();
  }

  /**
   * Calculate rate on price changes.
   */
  async getCalculatedRate(): Promise<void> {
    try {
      // Avoid api call in case of invalid field data
      if (!this.form.get('rate').valid) {
        this.form.patchValue({
          showCapRateBanner: false,
        });

        return;
      }
      const obj = {
        baseRate: typeof this.form.value.rate === 'string' ? parseFloat(this.form.value.rate) : this.form.value.rate,
        consumerId: this.job.client.id,
        timeAndPlace: {
          startTime: this.job.timestamps.startTime,
          endTime: this.job.timestamps.endTime,
          postCodeGeoId: this.job.location.postCodeGeoId,
        },
      };

      const netRate: NetRate = await this.jobsService.getLMJRates(obj);

      // check if net rate is exists
      if (netRate) {
        // get rate controls and update the rate from api response
        this.form.patchValue({
          netRate: netRate.rateWithFee,
          showCapRateBanner: netRate.exceedCapRate,
          priceLimitAlertType: this.getPriceLimitAlertType(netRate.priceLimitAlertType, netRate.priceLimitAlertToDType),
          capRate: netRate.capRate,
          dayOfWeekSelected: netRate.priceLimitAlertType,
          timeOfDaySelected: netRate.priceLimitAlertToDType,
        });

        // Emit the rate changes to parent component
        const parentFormPropertiesToBeUpdated: LastMinuteJobRateFormPropertiesToUpdate = {
          rate: this.form.value.rate,
          netRate: netRate.rateWithFee,
          capRate: netRate.capRate,
          dayOfWeekSelected: netRate.priceLimitAlertType,
          timeOfDaySelected: netRate.priceLimitAlertToDType,
        };
        this.rateChange.emit(parentFormPropertiesToBeUpdated);
      }
    } catch (error) {
      console.error('[LastMinuteJobRateComponent] getCalculatedRate error', error);
    }
  }

  /**
   * Returns a string representing what should be displayed on the price limit alert.
   *
   * @param dayOfWeek - priceLimitAlertType passed from the backend indicating day of the week.
   * @param timeOfDay - priceLimitAlertTodType passed from the backend indicating time of the day.
   * @returns - String representation of what the price limit alert type refers to.
   */
  private getPriceLimitAlertType(dayOfWeek: PriceLimitAlertEnum, timeOfDay: PriceLimitAlertToDEnum): string {
    if (timeOfDay) {
      return `${dayOfWeek}, ${TIME_OF_DAY_DESCRIPTION[timeOfDay.toLocaleLowerCase()]}.`;
    }

    return `${dayOfWeek}.` || 'Weekday between 6am - 8pm';
  }

  /**
   * Set properties need for the price limit alert banner.
   */
  private setPriceLimitAlertData(): void {
    const workerType = this.job?.workerFields?.match?.user?.profile?.myType;
    this.priceLimitAlertData = {
      agreementType: 'LMJ',
      agreementId: this.job?.workerFields?.agreement?.id,
      clientId: this.job?.client?.id,
      legacyAgreement: false,
      workerType: Array.isArray(workerType) ? workerType.toString() : workerType,
    };
  }

  /**
   * Starts listening to the rate input to check for value changes.
   * In case there's a change, function getCalculatedRate is triggered.
   */
  private rateInputListener(): void {
    this.form
      .get('rate')
      .valueChanges.pipe(debounceTime(500), takeUntil(this.onDestroySubject))
      .subscribe(() => {
        this.getCalculatedRate();
      });
  }

  /**
   * Gets the minimum rate.
   */
  private getMinimumAgreementRate$(): Observable<number> {
    return this.minimumRateService.fetchMinimumAgreementRate$();
  }

  /**
   * After setting the minimum rate variable, needed as a form validator, sets up the form.
   */
  private async setUpForm() {
    this.getMinimumAgreementRate$().subscribe((minimumRate) => {
      this.minimumRate = minimumRate;

      this.form = new FormGroup({
        rate: new FormControl<string | number>('', [
          Validators.required,
          Validators.pattern('^[0-9]+(.[0-9]{0,2})?$'),
          Validators.min(this.minimumRate),
          Validators.max(9999.99),
        ]),
        netRate: new FormControl<string | number>(''),
        capRate: new FormControl<string | number>(''),
        showCapRateBanner: new FormControl<boolean>(false),
        priceLimitAlertType: new FormControl<string>(''),
        dayOfWeekSelected: new FormControl<string>(''),
        timeOfDaySelected: new FormControl<string>(''),
      });

      this.rateInputListener();
    });
  }
}
